<template>
  <maps-main
    :centre="centre"
    :search-filters="mapSearchFilters"
    :visible-marker="visibleMarker"
    :data-lang="lang"
    @search-filters-updated="updateSearch"
    @marker-clicked="$emit('marker-clicked', $event)"
  >
    <nuxt-to-meta :nuxt2meta="head" />
  </maps-main>
</template>

<script lang="ts">
import { Component, Prop, toNative, Vue } from '~/utility/pu-class-decorator'
import { logger } from '../../utility/logger'
import { SearchFilters } from '../search/schemas'
import { SearchLinkGenerator } from '../url/generators/SearchLinkGenerator'
import { SearchFiltersUrl } from '../url/search-filters/SearchFiltersUrl'

const isDev = process.env.NODE_ENV !== 'production'

@Component({
  emits: ['mapMarkerOpened', 'marker-clicked'],
  setup() {
    const searchStore = useSearchStore()
    return { searchStore }
  },
})
class MapsPage extends Vue {
  get lang() {
    return this.$route.params.lang || 'en-gb'
  }

  @Prop({ required: false })
  hoveredCampsite?: string

  get head() {
    if (this.$isDesktop) {
      return {}
    }
    return {
      meta: [
        {
          hid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  }

  async updateSearch(searchFilters: SearchFilters) {
    logger('updateSearch', searchFilters)
    const path = this.$isDesktop
      ? this.$route.name?.includes('searchold')
        ? '/searchold/'
        : this.$route.name?.includes('searchnew')
          ? '/searchnew/'
          : '/search/'
      : '/maps/'
    const newUrl = this.$puLocalePath({
      path,
      query: SearchFiltersUrl.filtersToUrl(searchFilters).query as Record<
        string,
        string | string[]
      >,
    })
    if (newUrl === window.location.pathname + window.location.search) {
      return
    }
    if (isDev) {
      console.log('MapsPage.updateSearch', newUrl, searchFilters)
    }
    await this.$router.push(newUrl)
  }

  async mounted() {
    if (isDev) {
      console.log('MapsPage.mounted', this.$route.fullPath)
    }
    await this.handleBoundsAndHierarchyUrl()
  }

  destroyed() {
    if (isDev) {
      console.log('MapsPage.destroyed', this.$route.fullPath)
    }
  }

  private async handleBoundsAndHierarchyUrl() {
    // if we have bounds and path remove the bounds
    if (this.mapSearchFilters.bounds && this.mapSearchFilters.hierarchyPath) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { bounds, hierarchyPath, ...filters } = this.mapSearchFilters
      const linkGenerator = new SearchLinkGenerator()
      const urlData = linkGenerator.getUrlData({
        category: 'campsites',
        hierarchyPath,
        filters,
      })
      await this.$router.push({
        path: urlData.path,
        query: urlData.query as Record<string, string | string[]>,
      })
    }
  }

  get mapSearchFilters() {
    return this.searchStore.searchFilters
  }

  get visibleMarker() {
    return this.$route.query.visibleMarker || this.hoveredCampsite
  }

  get centre() {
    return this.$route.query.centre || this.$i18n.localeInfo.mapCenter
  }
}

export default toNative(MapsPage)
</script>
